import {
  assertEvent,
  assign,
  fromPromise,
  raise,
  setup,
  type SnapshotFrom,
} from 'xstate';

import {
  createEvent,
  getAvailability,
  getAvailabilityRange,
  noAvailability,
  updateEvent,
  createMoreAvailabilityEvent,
} from '../../services/schedularService';
import type {IGetAvailabilityQuery} from '../../types/dto/IGetAvailabilityQuery';
import type {ITimeSlots} from '../../types/ITimeSlots';
import type {IRequestedSlot} from '../../types/dto/IRequestMoreAvailabilityBody';

import type {
  IBookingMachineContext,
  IBookingMachineEvents,
  IBookingMachineInput,
  IBookingMachineOutput,
  SequenceDay,
} from './bookingMachine.types';
import {
  canNavigateNextWeek,
  hasSlotsInNormalRange,
  updateSequenceDays,
  getSelectedStartDateOfWeekFromSequenceDays,
  getSelectedTimeslots,
  generateSequenceDaysFromDayZeroDateChange,
  generateSequenceDaysFromAnyDayDateChange,
} from '../../utils/booking';
import type {IAdditionalQuestionAnswer} from '../../types/dto/IAdditionalQuestionAnswer';
import {
  addDays,
  formatISO,
  addTZDays,
  subTZDays,
  startOfTZDay,
  createTZDate,
} from '../../utils/date';
import type {AxiosError} from 'axios';
import {captureSentryMessage} from 'src/utils/sentry';
import type {IEvent} from '../../types/IEvent';

import {bookingMachineAnalyticsActions} from './bookingMachine.analytics';


export const bookingMachine = setup({
  types: {
    context: {} as IBookingMachineContext,
    events: {} as IBookingMachineEvents,
    input: {} as IBookingMachineInput,
    output: {} as IBookingMachineOutput,
  },
  actors: {
    getAvailabilityRange: fromPromise(
      async ({
        input,
      }: {
        input: {templateId: string; eventId?: string; sessionId?: string};
      }) =>
        getAvailabilityRange(input.templateId, {
          event_id: input.eventId,
          session_id: input.sessionId,
        }),
    ),
    getAvailability: fromPromise(
      async ({
        input,
      }: {
        input: {
          templateId: string;
          timezone: string;
          eventId?: string;
          sourceId?: string;
          startDate: string;
          endDate: string;
          sequenceDay?: number;
          sequencePrevDt?: string;
        };
      }) => {
        const params: IGetAvailabilityQuery = {
          viewer_timezone: input.timezone,
          event_id: input.eventId,
          source_id: input.sourceId,
          start_dt: input.startDate,
          end_dt: input.endDate,
          ...(input.sequenceDay && {
            sequence_day: input.sequenceDay,
          }),
          ...(input.sequencePrevDt && {sequence_prev_dt: input.sequencePrevDt}),
        };

        return getAvailability(input.templateId, params);
      },
    ),
    updateEvent: fromPromise(
      async ({
        input,
      }: {
        input: {
          eventId: string;
          date: string;
          startTime: string;
          timezone: string;
          attendee: string;
          firstAvailableDatetime: string;
          reason: string;
          aid: string;
        };
      }) =>
        updateEvent(input.eventId, {
          date: input.date,
          start_time: input.startTime,
          viewer_timezone: input.timezone,
          attendee: input.attendee,
          first_available_datetime: input.firstAvailableDatetime,
          reason: input.reason,
          aid: input.aid,
        }),
    ),
    sendNotification: fromPromise(
      ({
        input,
      }: {
        input: {
          templateId: string;
          guestName: string;
          guestEmail: string;
          guestPhone: string;
          sessionId?: string;
          guestTimezone: string;
          sequenceDay: number;
        };
      }) =>
        noAvailability(input.templateId, {
          sequence_day: input.sequenceDay,
          guest_name: input.guestName,
          guest_email: input.guestEmail,
          guest_phone: input.guestPhone,
          session_id: input.sessionId,
          guest_timezone: input.guestTimezone,
        }),
    ),
    requestMoreAvailability: fromPromise(
      async ({
        input,
      }: {
        input: {
          templateId: string;
          guestName: string;
          guestEmail: string;
          guestPhone: string;
          timezone: string;
          language: string;
          sessionId?: string;
          additionalQuestionAnswers: IAdditionalQuestionAnswer[];
          source?: string;
          requestedSlots?: IRequestedSlot[];
          requestReason?: string;
        };
      }) => {
        return createMoreAvailabilityEvent({
          template_id: input.templateId,
          guest_name: input.guestName,
          guest_email: input.guestEmail,
          guest_phone: input.guestPhone,
          guest_timezone: input.timezone,
          guest_language: input.language,
          session_id: input.sessionId,
          source: input.source,
          requested_slots: input.requestedSlots,
          request_reason: input.requestReason,
          additional_question_answers: input.additionalQuestionAnswers,
        });
      },
    ),
    createEvent: fromPromise(
      async ({
        input,
      }: {
        input: {
          templateId: string;
          timezone: string;
          date: string;
          startTime: string;
          guestName: string;
          guestEmail: string;
          guestPhone: string;
          firstAvailableDatetime: string;
          sessionId?: string;
          language: string;
          additionalQuestionAnswers: IAdditionalQuestionAnswer[];
          selectedTimeslots: Array<{
            day: number;
            date: string;
            start_time: string;
          }>;
        };
      }) => {
        return createEvent({
          template_id: input.templateId,
          viewer_timezone: input.timezone,
          date: input.date,
          start_time: input.startTime,
          guest_name: input.guestName,
          guest_email: input.guestEmail,
          guest_phone: input.guestPhone,
          first_available_datetime: input.firstAvailableDatetime,
          additional_question_answers: input.additionalQuestionAnswers,
          session_id: input.sessionId,
          guest_language: input.language,
          selected_timeslots: input.selectedTimeslots,
        });
      },
    ),
  },
  guards: {
    isTimeSlotUnavailable: ({context}) => {
      const error = context.error as AxiosError<{errors: string[]}>;
      const errorKey = error?.response?.data?.errors?.[0];
      const unavailableTimeSlotErrorKeys = [
        'UnavailableTimeSlot',
        'TimeSlotNotAvailable',
      ] as const;
      return unavailableTimeSlotErrorKeys.includes(
        errorKey as typeof unavailableTimeSlotErrorKeys[number],
      );
    },
    isSlotRequestAlreadyExists: ({context}) => {
      const error = context.error as AxiosError<{errors: string[]}>;
      const errorKey = error?.response?.data?.errors?.[0];
      const unavailableTimeSlotErrorKeys = [
        'SlotRequestAlreadyExists',
      ] as const;
      return unavailableTimeSlotErrorKeys.includes(
        errorKey as typeof unavailableTimeSlotErrorKeys[number],
      );
    },
    isSlotsAvailableForCurrentWeek: ({context}) => {
      const {sequenceDays, currentSequenceNumber, availabilityRange} = context;

      if (!availabilityRange) {
        throw new Error('Availability range is not found');
      }

      const sequenceNumber = currentSequenceNumber ?? 0;
      const selectedDay = sequenceDays?.find(
        (day) => day.sequenceNumber === sequenceNumber,
      );
      const selectedStartDateOfWeek = selectedDay?.selectedStartDateOfWeek;
      const timeSlots = selectedDay?.timeSlots;

      if (!selectedStartDateOfWeek) {
        throw new Error(
          'Selected start date of week is not found for current day',
        );
      }

      if (!timeSlots) {
        return false;
      }

      const weekStart = formatISO(selectedStartDateOfWeek, {
        representation: 'date',
      });
      const weekEnd = formatISO(addDays(selectedStartDateOfWeek, 6), {
        representation: 'date',
      });

      const hasAvailableSlotsInCurrentWeek = Object.keys(
        timeSlots.free_time_slots || {},
      ).some((dateKey) => {
        return dateKey >= weekStart && dateKey <= weekEnd;
      });

      return hasAvailableSlotsInCurrentWeek;
    },
    isNewBooking: ({context}) => !context.eventDetails,
    isNoSlotsAvailableTillLastWeek: ({context}) => {
      const {
        sequenceDays,
        currentSequenceNumber,
        availabilityRange,
        timezone,
        eventTemplate,
      } = context;

      if (!availabilityRange) {
        throw new Error('Availability range is not found');
      }
      const selectedStartDateOfWeek =
        getSelectedStartDateOfWeekFromSequenceDays(
          sequenceDays,
          currentSequenceNumber ?? 0,
        );

      if (!selectedStartDateOfWeek) {
        throw new Error(
          'Selected start date of week is not found for current day',
        );
      }

      const currentSequenceDay = sequenceDays?.find(
        (day) => day.sequenceNumber === currentSequenceNumber,
      );
      const timeSlots = currentSequenceDay?.timeSlots;
      const hasSlotsAvailableTillCurrentWeek =
        Object.keys(timeSlots?.free_time_slots || {}).length > 0;
      const showExtendedAvailabilityByDefault =
        eventTemplate?.extended_future_limit?.policy === 'always';

      const hasSlotsInNormalRangeTillCurrentWeek = hasSlotsInNormalRange(
        timeSlots as ITimeSlots,
        availabilityRange.end_at,
        timezone,
      );

      const canNavigateToNextWeek = canNavigateNextWeek({
        timezone,
        selectedStartDateOfWeek,
        availabilityRange,
        hasNoSlotsInNormalRange: !hasSlotsInNormalRangeTillCurrentWeek,
        currentSequenceDay: currentSequenceNumber,
        showExtendedAvailabilityByDefault,
      });

      /**
       * If there are no slots available till current week and also we cannot navigate to the next week. that means user is on last week without any slots.
       */
      return (
        Boolean(!canNavigateToNextWeek) &&
        Boolean(!hasSlotsAvailableTillCurrentWeek)
      );
    },
    isDayZero: ({context}) => {
      return (context.currentSequenceNumber ?? 0) === 0;
    },
    isRescheduleOrCancellationNotAllowed: ({
      context,
    }: {
      context: IBookingMachineContext;
    }) => {
      return (
        Array.isArray(context.eventDetails) &&
        context.eventTemplate?.distribution_rule === 'sequential_interview'
      );
    },
    shouldStartAtRequestDetailsStep: ({context}) => {
      const hasSlotRequestRule = Boolean(
        context.eventTemplate?.rules?.find(
          (rule) => rule.rule_type === 'slot_request_rule',
        ),
      );
      return (
        Boolean(context.releaseFlags?.isRequestAvailabilityPhaseTwoEnabled) &&
        hasSlotRequestRule
      );
    },
  },
  actions: {
    ...bookingMachineAnalyticsActions,
  },
}).createMachine({
  /** @xstate-layout N4IgpgJg5mDOIC5QCMD2qDWBLAdlAxADYCGeArsTAHRkAOExALmANoAMAuoqLarFoyyoc3EAA9EAWgCMAFgBsAdioAmAJwBWDfLZaAzPJUaAHABoQAT0TTpKvVQ1sVbWWtnTF0x9IC+P82iYuFBUAMYAFmChGABKcBGQZIRgAPIATgDCpKFghCSCwgByqIwAgnmoAO6Q+OxcSCC8-AUiDRIIkoqK8lTGakpsxsYKsnom5lYIxvL2anYzzrZ6xrZ+AejYeGGR0XGwCRBJqZnZuflCOMVlFdUQtdL1PHwCF6LtkstsvRoqznpecjYOnkE0Q01m8z0izsKzWIECmxCxAAbsQsCRkOiBBYYqQYAAZVDECDBfAQYRgKi4ZGYSkI4JUFFojFYxg4vFgQnE4IIamoUJMC51OqiJovYRvKSLKh6XTGDRqRRA+ZmSyIfo9NTGTxsDzTExauH0rZM9HETGEbG4vCcokkvD4MBpNKoNJUWj5ABmroAtlRjUjUWaLVaOVz7VBeTgaQKWsLOKLni1JQgVLIVL0dLq2Gw9LKTIo9KCEJoM25dWnZYWVPJfP54RsGQxmAAVLA+sAABSw0Sd+FgjGIaUYABEmKlPQB1MBgDDbDkihpi5NtaxfDQ2ZZeJXy4xOYs2OZUUbGMYbn5atR6I2NrbNsBtjvd3tpMnj+c2xdPZqvVcdKH2DWbD9J4sqKl4GgHnM9hAho-x6JoihqDm171gGVD3o+XY9hgfbVLOVA4GAYiMF+jRJr+oDvEMXw1nBwFOLYrgqAeKheMesiyI4hhKkq8jyDeQR3uOWHPrhr74XOtBpGAyJkculHiFINEysh-wmKe3gsWqCDSAYsjHooKjTEo8haLI0yCYiGEie22Evm+FiEcRpEJkuFESn+Mi1jKCHGfIe4KshiiseoVCDN4RmyEqchWU2tlPjhfYME50myfJHmtFRUgbtIVBqSoijRa4XR6CFOl6dIaiqNFtYVlV6ayHFwmtnZYl9jJACOZBwIwACyrpgKUQYspabIAGK+lQUCoBlP6edlHQ6qoNgLDxbBIWoxY1mMVA6LlXFsb8GjNSEmFtUlbqmqN2LhqS5JEVS0a0v6t5nQl9niYyI3mqyFh3XgUYxoKwjxo85HzVlSlTPYJjSEoGiKNoch6aqkzw2VVBdExG6LLqAloW9NmtYlL7fcyv1jf9dqkk6Lpuh6TDemkfroedpNfddlO3TTgN8rGQqcHN4pQ+0GjRaoIGlsxSNbRVeZ5aZ8OKmV6Z6adxMPhdZNcyGbLhjUwsrotu3Ic4G0rIMYzalBFnhe4mhAi48qFRr7OfU65PBn9Bt3CwDyJpDKYmGWxjOLWUIBWV5Xo24xj29I8pmQBgxqG7H3tVdP169TxKGyo4MKQt0P8V8wFyKM-S6L8II6YseVaGZwzSE4l6KOnJMe1nFM5wAos6rr4DJjBpBYRuKe0sjAXtnG6umSjGVWrGGCt-yLE3BgqB3Wsc57ut-QAyoQJSwPgggdrAx+MGEwielgLPj8X7R9PlRhcY4uraMMaOIGx8jVQYNQidkIWTTNvUSl0qDnzgFfDIt974+hBjgM+dlL4lBvjgO+D83LfhFimKqyguJ7hcPDboWYf6pi4oQv+OYtR7mGOA7WX1oFoMYHAzBCCkH4ADJNFm01Zo4IhngryzgHDyFGBHXGdD5TbW0MoeGyoEZFTUG4Rhu83QsNgfAlmXCZL7EiIcZIcRiCwGELwv0M1H6i0QMsaqSojBFVsECTwFCjA+WmHpfo7gEJ9DUV3KBqCtEcJ0S0bhxBohWJTDIZQziTDW08IsWQ21KwyhrKeRU5cp4E3WEJd6ndM4BIvkErBiDQldR6gOAaMlho9z+uY-hkSvK2C0HtZpehZ56V+Ekuu4s7FT0CkjLQ6g-EFP3lTK4WBPQWHwDgEokyLAAAk+DX0sYIou1jdLtI0PlAshYLLaiUFBHQYijJQinsZGwTVCa5M1hAnW2c-oTKmY5d0Mk5JrMyqIWO4twqI2MvRUYbFVTtE6fYMYSMuhzAUD8YwGsxmhhtP3emQ8wAjzHh8oOf4nFUC8K4Povw8yym0pMeUeVm5lR+PxJQdYcnWVmYIZ5yBwkYEaaAb5PQfheF+JuFwCpzDtDTAFb42ofg-ChMBdu1y6VzKmVQZmPoZkyqcrAMgyAfQCFZdDDwdt3AWWhfxUYSNtpyHsNqTQX9OWFg1vS+ZVAVVqoEIIB0D1KR8lwq9G5NrZX2vVYwJ1kZ+ZILBoHYRi02JKnCgvFRhYCzDG2u0no7hLm4sGAQ61Sq7Wqt9f6x0A8GZeimuhL1yqs2Op5IGuMQsMWhq1So6qipxbyl0AqYlv95hYzUiYWe4d00MqcnTQexbM0OtcoXT5f5FjKDGE4T4Ydxb-2NaMXoSEuJyH8gYDWPDfTcLevUplETq3G2hh8LivQXZGS-mVIw20iHHiGLYLoYc3BdE3buqa8r8DyuHb6zV7xgSqEbkYDaSN2nbT6NsxQZrkang8NkhsNyt18J9WW51FIno0ndehRDfpkN+vLc9AWoMq1jsxYtSQiMejqEcO0x9OYVjFjzECVQuYlBFUGO4SyUqGTYe-ShggA781M0LUTHjuH-VA35EG4jIaj3vBrAZQV+pXABS8LXSY-wmM2DkHMOqBrX25PqQJ2oh6J42IMkAsYKjE5iqGN0yY6gfISu8Keex4j9OIkM3m+4JGa3vArseOCTdayFiMjbOu-Q8pOd1C5pwbmuNbB48PUeB9S2ME-VNXDv6pAWR6DRJ2Yx4bi04ttTQ8cPC6DlNWPM7ngj1LIDgXWyQsJHxKCiz0qKIg1O9lTBpJmn5SGVpGuioCzlDDU7-W9+yH2QfUBZDW5TepVKGg8qm5iUXdUW4NLrN0JqFuZVljoEFqqGEcBRgKaTZGuCxv03QgyLzzbABtypW2VvYnqRAVFTp1VEV7jgNFB9mC0GMz52TA3NCw0RumQsbhVpgaPMBOOcEFRFSubShkC3nvVNe7tvhH3mAs1wGAX7-3AfeZk6Zw7eZ45ALkHucR1txupnxeFFRFkkeKk4g9p7-UXu1NW1NDHo5UXMlPoLpb23uZsioILkcwv0SwGciRA70S2IzxR9-dQoWGO5jBXxc2RkzLRS5xUnnWO+dvYF49k3ToACSmDfS6Kt5ts33XsTS6dwOW39v-T7b6xs6JVUZSPvAm-fokFwsbgA+LOCQDYKaGN875b5ucd+kF17+Vjvufi+x05NPaQ7fel46O8n-XDuuAMojMOugZieBbhQjT8dF65hjyjuwCfMdJ9dynov-qWuMDiNzskaG3V0iJmL3nXeLD1LE8EPvA+TcScIzgYN7lSPHq8JjGDnFVZaA8K2hA7TpgJzmBBVwWh2+m87ztqfGXUu96vvP3qub6bugLXw9C4+XfX+n3f2fD+PeMCL5SYcDK42C-B7RDDGRIxTYuAMZ5gZhmQtyFjyijAbhwYf4AHZ7J4358JGbK4w5YzaAITtKHhyCM5ViASIy5iniMQ-AX5YGT6ebIr+wg4U4yAWw1RUanjyiOBzDa5KCSzAQrDiLdD9CwrxYhCf5X6S44Gp6orJa-54Bz4AHrYm4MHX7u7c7p6oBF6gGJxfBVRwQFY2DNz16-DVTwwrA7j-xTz9AaysKP4DjlAyTEgWC9xiBYADii5gDtaMCdY569asGl6SBAKN7V4uC5jzByz2ajAGRNxdBsRxLdAnQSHu76KJDJBrZ6IHBHDGKmI4B7q+5BEbJRw4q1T-CGDGRALRG-yIyATIG-DDCFQIQPbpGGJgD1LZEGK5FgAmJmLbpdEZFgB5HCA0D0DjgHbTDxyQZITaAzqipgaq7GTuAWxIyrCpGDHtGdHxDdFGK9H5FraaLoKhDaI+gHbiwIHuD-yaSsaHIR49BlQBRwQxbLCtE5GZGW5tFHDmIpYjo5ouroYvQYFfEfF8KbHfG+i-HZr4bAyVogF+7Bw-BYzDCIzmTqTwxwHwyqCQZ6RITah7heBvG7EdGfHvEkksxQl8bP6uiv5Cbv5j47FDHbEgnkk+iUl4Z8wEbAGTFODhQJHtKKhJwIT8F5SXg0TyYeKoRo5bDgmgl+h4EIlYrpj2COK4pITlZGQlYmB7Q1j-yFRzDaCGBElMlTR4EByr6+bWAXJYz+QEnKbwElYGA1Q5hApKjITx4bGMlbFTT1aNY7xgB968J7BkltYdbhAS45xBlelHCBEl4bK4m5ZxKVG2ajDxo5h7QRxFSCrATGnelgnyE4jRlynskoporBnEk-GKFQAHZGRfAo6KhXiXKNwlaeDhS6Db7dCkJxb1izIfbwANABhxlRLOBXZaBZnQ5JqM6153o2CgSoxMQawJC7BFnHBZA4A5B5BIJXDlDHy3BDlNK-DKBFQJozD-w6D74eJ7SFhZjbgzAtGekslrkbnnBFAlA7lVCQD7lkZrE4p+RARKAonFj9D2APriJzCDCWxbypHwpsjWgEi8xQBfnHrOCEIGDLDCFOCeAzDFgon5QrC06WHdCo7wbWTuyZxIXvBaD1qRFFTKKlQxzWAtzmZbhhwWSCm6AjKQIwW5wRgUWIARpAg6CQbzycQqJFjyxGQBYCmQZ5jCWcX3LYG+x8VTDbI8GrTRSPp9AUIeBajHilxQ6FTiLGTyWcw55IqujKXaAKYtxQGQEqZ2ZWkrxsTirNoOIMKpFkVcU55979m4Kg66S6jfAOZGCNoXIMWpi3Y4qJwXJsSgrEVswZxeXYG9w+i0BsjKXiJ1lUXQ5ZmGDbRCFYzARsSYUI6c4eWJVkxHFsKnFILKVFStIOaIyzlaiQbxpXZVyiqqY-A1gmV7w55PKTB+UU5MXVSrpQhISCowpQQ-K3b-KyiAqJxwo55wVE55rKVAZ8lmoeDdAGBIQ4WR7kpIy6nUo1ZKFkChA5CwC+VCL+VMUOBlQbT6ELyWZwG8lXiwSpqxUpHSkhDFopYXVwDXXrIphsS6V2C8GDDiLaA1g3ozD5SQ3GQXJQht6pHFrKXwyOYzHPqQZDDaWaAcp-xIQ6CmHfUkUMhDryro04nHg1hrHcpwzh72YuAGS0Hcp6S8ppyo0Zoz54BU3TyrHY242uJpiimFbggWyc0-WEQZoCbrV2B5TTrlgzrOCKiLqmorobjpjgjoEiZvoszKWSBKAGSFaGAuDqCRGM2-yha9Cs5YWJwOySpS08aU0Wn+XkZdC-mcQrDRSQ7qBgZYkISNnuB2AuCO1k0JZ604ZVkG2GA9Am0jnm1QiW0H4bRfBiEx4AhG2nVQBMEWWu1sFkoIylxag-DFRGo6T-AeAyjASmSdlm1Snh0hCJYFnskG0VGZibRyLqArDJ1cG9A+2g2nlGTZ11YNbZxNZ2R97KUSxeIISxEqxiVgZISvwI5h4bTBT0ET7X7-WXVA3jpkZeDITHgVhTwVFJEMbQ1noamHR7I603JSERl1K+gG3mwNy+0Tmw51wuLH3RrRYtwqKb1f4yHvafYE4-Z-bJaA4G1VRMa1gq21ini1hyBga6D5QtzKgiqfxh0YFZ5b3AOW7c6y6Djy7QPBQAbarPHITe0lZw16jQrsYzCAPSGRkEPW75724hKKTA1NIkF7QIRiqGA93GRal5QhVcQIQtxmQelS0P054-5-F-4lCOGMDQPRQqmDBsYXpai6AMbODbL8Q0Eok2UKBMOP3864FrX53BGsUrQzClbZj-DhV5g-IPpUVTyOKS2N2aFqF4MsP5n-ZVnKHc7QMPpBV5ihFhwoEMa05RVwQ7UxSuD2H-7c7OG9EQBuEeFeEG1XgZhFTyhuDqBuDiKuK9LXYSpWygK5kQn61WMbLRRfDpgbihGVGcRcTbSnKZjnkbhmpE1VNylpFkkjEFHP21PByB66mzm5jN7jB1z4UyhBaiFATYMMksnMlkmVkKO82jN-jdM01NOJwtPFYV26B2LwxcRTyOB2DxUrPrOmmWNDWl517yIn4KBGQJKpnHOni2NZK2AXKePAm3N8K+nj3+mBmujllDHo3LAIGOOsW6p2Bga7REGNE5aBR9Osnu5lkrkbO+ro1OAZhhVajwEGquL9Chx6ggIxV+B+BAA */
  context: ({input}) => input,
  id: 'booking',
  initial: 'checkRescheduleOrCancellationNotAllowed',
  entry: 'trackMeetingLinkVisited',
  on: {
    'language.update': {
      actions: assign({
        language: ({event}) => {
          return event.language;
        },
      }),
    },
  },
  states: {
    checkRescheduleOrCancellationNotAllowed: {
      always: [
        {
          target: 'rescheduleCancellationNotAllowed',
          guard: 'isRescheduleOrCancellationNotAllowed',
        },
        {
          target: 'availabilityRangeLoading',
        },
      ],
    },

    rescheduleCancellationNotAllowed: {},

    availabilityRangeLoading: {
      invoke: {
        id: 'booking.availabilityRangeLoading:invocation[0]',
        input: ({context: {eventDetails, session, eventTemplate}}) => {
          const templateId =
            eventTemplate?.id ||
            (eventDetails as IEvent)?.scheduled_from_template_id;
          if (templateId) {
            return {
              templateId,
              eventId: (eventDetails as IEvent)?.id,
              sessionId: session?.id,
            };
          }
          throw new Error('Template id not found');
        },
        onDone: {
          target: 'dateTimePicker',
          actions: assign({
            availabilityRange: ({event}) => event.output,
            currentSequenceNumber: 0,
            sequenceDays: ({event, context}) => {
              const startAt = event.output.start_at;
              if (!startAt || isNaN(startAt)) {
                captureSentryMessage(
                  'Invalid start_at from Availability Range API',
                  {
                    level: 'error',
                    tags: {
                      product: 'scheduler',
                    },
                    extra: {
                      apiParams: {
                        templateId: context.eventTemplate,
                        sessionId: context.session?.id,
                        eventId: (context.eventDetails as IEvent)?.id,
                      },
                      timezone: context.timezone,
                      rangeApiResponse: event.output,
                      eventDetails: context.eventDetails,
                      eventTemplate: context.eventTemplate,
                    },
                  },
                );
              }
              const startDayOfWeekFromRangeApi = createTZDate(
                startAt * 1000,
                context.timezone,
              );
              return [
                {
                  sequenceNumber: 0,
                  selectedStartDateOfWeek: startDayOfWeekFromRangeApi,
                  selectedDate: undefined,
                  selectedTimeSlot: undefined,
                },
              ];
            },
          }),
        },
        onError: {
          target: 'availabilityRangeError',
          actions: assign({
            error: ({event}) => event.error,
          }),
        },
        src: 'getAvailabilityRange',
      },
    },

    dateTimePicker: {
      initial: 'availabilityLoading',
      on: {
        'startDateOfWeek.change': {
          target: '#booking.dateTimePicker.availabilityLoading',
          actions: assign({
            sequenceDays: ({context, event}): SequenceDay[] => {
              // Preserve existing timeslots when changing week for sequence 0
              const existingDayZero = context.sequenceDays?.[0];
              return [
                {
                  sequenceNumber: 0,
                  selectedStartDateOfWeek: event.date,
                  selectedDate: existingDayZero?.selectedDate,
                  selectedTimeSlot: undefined,
                  timeSlots: existingDayZero?.timeSlots,
                },
              ];
            },
          }),
        },
        'date.change': {
          actions: assign({
            sequenceDays: ({context, event}): SequenceDay[] => {
              const {
                sequenceDays = [],
                eventTemplate,
                timezone,
                currentSequenceNumber = 0,
              } = context;
              const changedDaySequenceNumber = currentSequenceNumber;

              const generatedSequenceDays =
                generateSequenceDaysFromAnyDayDateChange({
                  changedDaySequenceNumber,
                  newDate: event.date,
                  existingSequenceDays: sequenceDays,
                  sequence: eventTemplate?.metadata?.sequence,
                  timezone,
                });

              return generatedSequenceDays;
            },
          }),
          target: 'dateTimePicker.availabilitySlots',
        },
        'week.next': {
          guard: 'isDayZero',
          actions: raise(
            ({context: {sequenceDays, currentSequenceNumber, timezone}}) => {
              const sequenceNumber = currentSequenceNumber ?? 0;
              const selectedStartDateOfWeek =
                getSelectedStartDateOfWeekFromSequenceDays(
                  sequenceDays,
                  sequenceNumber,
                );
              if (!selectedStartDateOfWeek) {
                throw new Error('Selected start date of week is not found');
              }
              return {
                type: 'startDateOfWeek.change',
                date: addTZDays(selectedStartDateOfWeek, 7, timezone),
              };
            },
          ),
        },
        'week.prev': {
          guard: 'isDayZero',
          actions: raise(
            ({context: {sequenceDays, currentSequenceNumber, timezone}}) => {
              const sequenceNumber = currentSequenceNumber ?? 0;
              const selectedStartDateOfWeek =
                getSelectedStartDateOfWeekFromSequenceDays(
                  sequenceDays,
                  sequenceNumber,
                );
              if (!selectedStartDateOfWeek) {
                throw new Error('Selected start date of week is not found');
              }
              return {
                type: 'startDateOfWeek.change',
                date: subTZDays(selectedStartDateOfWeek, 7, timezone),
              };
            },
          ),
        },
        'day.next': {
          target: '#booking.dateTimePicker.availabilityLoading',
          actions: assign({
            currentSequenceNumber: ({context}) =>
              (context.currentSequenceNumber ?? 0) + 1,
          }),
        },
        'day.prev': {
          target: '#booking.dateTimePicker.availabilityLoading',
          actions: assign({
            currentSequenceNumber: ({context}) =>
              (context.currentSequenceNumber ?? 0) - 1,
          }),
        },

        'requestMoreAvailabilityForm.go': {
          target: '#booking.requestMoreAvailabilityForm',
        },
      },
      states: {
        availabilityLoading: {
          invoke: {
            input: ({
              context: {
                eventDetails,
                eventTemplate,
                timezone,
                session,
                availabilityRange,
                sequenceDays,
                currentSequenceNumber,
              },
            }) => {
              const templateId =
                eventTemplate?.id ||
                (eventDetails as IEvent)?.scheduled_from_template_id;
              try {
                if (!templateId || !availabilityRange) {
                  throw new Error('Template id or availabilityRange not found');
                }

                const sequenceNumber = currentSequenceNumber ?? 0;
                const selectedStartDateOfWeek =
                  getSelectedStartDateOfWeekFromSequenceDays(
                    sequenceDays,
                    sequenceNumber,
                  );

                if (!selectedStartDateOfWeek) {
                  throw new Error(
                    'Could not determine selectedStartDateOfWeek for current day',
                  );
                }

                const currentDate = selectedStartDateOfWeek;
                const addedDate = addTZDays(currentDate, 7, timezone);
                const startDate = formatISO(
                  startOfTZDay(selectedStartDateOfWeek, timezone),
                  {
                    representation: 'complete',
                  },
                );
                const endDate = formatISO(startOfTZDay(addedDate, timezone), {
                  representation: 'complete',
                });

                // Only include sequence_day and sequence_prev_dt if sequenceNumber is not 0
                let sequenceDay: number | undefined;
                let sequencePrevDt: string | undefined;

                if (sequenceNumber !== 0) {
                  sequenceDay = sequenceNumber;
                  const prevSequenceNumber = sequenceNumber - 1;
                  const prevDay = sequenceDays?.find(
                    (day) => day.sequenceNumber === prevSequenceNumber,
                  );
                  const startTimeOfPrevDay =
                    prevDay?.selectedTimeSlot?.start_time;

                  // sequencePrevDt is the start time of the previous day with timezone
                  sequencePrevDt =
                    prevDay?.selectedDate && startTimeOfPrevDay
                      ? formatISO(
                          createTZDate(
                            `${prevDay.selectedDate}T${startTimeOfPrevDay}:00`,
                            timezone,
                          ),
                          {
                            representation: 'complete',
                          },
                        )
                      : undefined;
                }

                return {
                  templateId,
                  timezone,
                  eventId: (eventDetails as IEvent)?.id,
                  sourceId: session?.source_id,
                  startDate,
                  endDate,
                  sequenceDay,
                  sequencePrevDt,
                };
              } catch (error) {
                captureSentryMessage('Error setting availability api params', {
                  level: 'error',
                  tags: {product: 'scheduler'},
                  extra: {
                    error,
                    timezone,
                    eventDetails,
                    eventTemplate,
                    session,
                    availabilityRange,
                    sequenceDays,
                    currentSequenceNumber,
                  },
                });
                throw error;
              }
            },
            onDone: {
              target: 'availabilityLoaded',
              actions: assign({
                sequenceDays: ({context, event}) => {
                  const {
                    sequenceDays,
                    currentSequenceNumber,
                    eventTemplate,
                    timezone,
                  } = context;
                  const firstAvailableDate = Object.keys(
                    event.output?.free_time_slots || {},
                  )[0];
                  const sequenceNumber = currentSequenceNumber ?? 0;

                  if (sequenceNumber === 0) {
                    const dayZeroSequenceDay = {
                      ...(sequenceDays?.[0] ?? {}),
                      selectedDate: firstAvailableDate,
                      timeSlots: {
                        free_time_slots: {
                          ...sequenceDays?.[0].timeSlots?.free_time_slots,
                          ...event.output?.free_time_slots,
                        },
                      },
                    };
                    return generateSequenceDaysFromDayZeroDateChange({
                      dayZeroSequenceDay: dayZeroSequenceDay as SequenceDay,
                      sequence: eventTemplate?.metadata?.sequence,
                      timezone,
                    });
                  }
                  // else update the selected date and time slot for the current day
                  return updateSequenceDays(sequenceDays, sequenceNumber, {
                    ...(sequenceDays?.[sequenceNumber] ?? {}),
                    selectedDate: firstAvailableDate,
                    timeSlots: event.output,
                  }) as SequenceDay[];
                },
              }),
            },
            onError: {
              target: 'availabilityError',
              actions: assign({
                error: ({event}) => event.error,
              }),
            },
            src: 'getAvailability',
          },
        },

        availabilityLoaded: {
          always: [
            {
              target: 'availabilitySlots',
              guard: {
                type: 'isSlotsAvailableForCurrentWeek',
              },
            },
            {
              target: 'availabilityNotify',
              reenter: true,
              guard: 'isNoSlotsAvailableTillLastWeek',
            },
            {
              target: 'availabilityEmpty',
              reenter: true,
            },
          ],
        },

        availabilityError: {
          on: {
            retry: {
              target: 'availabilityLoading',
            },
          },
        },

        availabilitySlots: {
          on: {
            'timeslot.confirm': {
              target: 'timeslotConfirmation',
              actions: assign({
                sequenceDays: ({context, event}): SequenceDay[] =>
                  updateSequenceDays(
                    context.sequenceDays,
                    context.currentSequenceNumber ?? 0,
                    {
                      selectedTimeSlot: event.timeSlot,
                    },
                  ) as SequenceDay[],
              }),
            },
          },
        },

        availabilityEmpty: {},

        timeslotConfirmation: {
          on: {
            'timeslot.confirm': {
              target: 'timeslotConfirmation',
              actions: assign({
                sequenceDays: ({context, event}): SequenceDay[] =>
                  updateSequenceDays(
                    context.sequenceDays,
                    context.currentSequenceNumber ?? 0,
                    {
                      selectedTimeSlot: event.timeSlot,
                    },
                  ) as SequenceDay[],
              }),
            },

            'bookingForm.go': {
              target: '#booking.bookingForm',
              guard: 'isNewBooking',
            },

            'rescheduleReasonForm.go': {
              target: '#booking.rescheduleForm',
            },

            back: {
              target: 'availabilitySlots',
              actions: assign({
                sequenceDays: ({context}): SequenceDay[] => {
                  // Always clear Day 1's time slot (sequenceNumber 0)
                  return updateSequenceDays(context.sequenceDays, 0, {
                    selectedTimeSlot: undefined,
                  }) as SequenceDay[];
                },
                // If on Day 2 or later, go back to Day 1 (sequenceNumber 0)
                currentSequenceNumber: ({context}) => {
                  const sequenceNumber = context.currentSequenceNumber ?? 0;
                  return sequenceNumber > 0 ? 0 : sequenceNumber;
                },
              }),
            },

            'requestMoreAvailabilityForm.go': {
              target: '#booking.requestMoreAvailabilityForm',
            },
          },
        },

        availabilityNotify: {
          on: {
            'notifyHost.go': {
              target: '#booking.notify',
              reenter: true,
            },
            'day.prev': {
              target: 'availabilityLoading',
              actions: assign({
                currentSequenceNumber: ({context}) =>
                  (context.currentSequenceNumber ?? 0) - 1,
              }),
            },
          },
        },
      },
    },

    availabilityRangeError: {
      on: {
        retry: {
          target: 'availabilityRangeLoading',
        },
      },
    },

    bookingSuccess: {
      type: 'final',
      entry: assign({
        finalState: 'BookingSuccess',
      }),
    },

    notifySuccess: {
      type: 'final',
      entry: assign({
        finalState: 'NotifySuccess',
      }),
    },

    notify: {
      initial: 'form',
      on: {
        back: {
          target: '#booking.dateTimePicker.availabilityLoading',
          actions: assign({
            currentSequenceNumber: ({context}) =>
              (context.currentSequenceNumber ?? 0) - 1,
          }),
        },
      },
      states: {
        form: {
          on: {
            'notify.submit': {
              target: 'submitting',
            },
          },
        },

        submitting: {
          invoke: {
            input: ({
              context: {
                session,
                eventTemplate,
                eventDetails,
                timezone,
                currentSequenceNumber,
              },
              event,
            }) => {
              assertEvent(event, 'notify.submit');
              const templateId =
                eventTemplate?.id ||
                (eventDetails as IEvent)?.scheduled_from_template_id;
              if (!templateId) {
                throw new Error('Template id not found');
              }
              return {
                templateId,
                guestName: event.guestName,
                guestEmail: event.guestEmail,
                guestPhone: event.guestPhone,
                sessionId: session?.id,
                guestTimezone: timezone,
                sequenceDay: currentSequenceNumber ?? 0,
              };
            },
            onDone: {
              target: '#booking.notifySuccess',
              actions: 'trackNotifyHostSubmitted',
            },
            onError: {
              target: 'error',
              actions: assign({
                error: ({event}) => event.error,
              }),
            },
            src: 'sendNotification',
          },
        },

        error: {
          on: {
            'notify.submit': {
              target: 'submitting',
            },
          },
        },
      },
    },

    bookingForm: {
      initial: 'form',
      states: {
        form: {
          on: {
            'form.submit': {
              target: 'submitting',
            },
          },
        },
        submitting: {
          invoke: {
            input: ({
              context: {
                eventTemplate,
                timezone,
                sequenceDays,
                session,
                language,
              },
              event,
            }) => {
              assertEvent(event, 'form.submit');

              // Get date and start time from day 0
              const {selectedDate, selectedTimeSlot, timeSlots} =
                sequenceDays?.[0] || {};
              const date = selectedDate;
              const startTime = selectedTimeSlot?.start_time;

              if (!timeSlots) {
                throw new Error('Time slots not found for current day');
              }

              // get formatted date and start time from all days
              const selectedTimeslots = getSelectedTimeslots(sequenceDays);

              const firstAvailableDate = Object.keys(
                timeSlots.free_time_slots || {},
              )[0];
              const firstAvailableTimeList =
                timeSlots.free_time_slots[firstAvailableDate];
              const startAvailableTime = firstAvailableTimeList?.[0].start_time;

              if (eventTemplate?.id && date && startTime) {
                return {
                  templateId: eventTemplate?.id,
                  timezone,
                  date,
                  startTime,
                  guestName: event.guestName,
                  guestEmail: event.guestEmail,
                  guestPhone: event.guestPhone,
                  firstAvailableDatetime: `${firstAvailableDate} ${startAvailableTime}`,
                  sessionId: session?.id,
                  language,
                  additionalQuestionAnswers:
                    event.additionalQuestionAnswers || [],
                  selectedTimeslots,
                };
              }
              throw new Error(
                'Template id, selected date or selected time slot not found',
              );
            },
            onDone: {
              target: '#booking.bookingSuccess',
              actions: [
                assign({
                  eventDetails: ({event}) => event.output,
                }),
                'trackBookingConfirmed',
              ],
            },
            onError: {
              target: 'error',
              actions: [
                assign({
                  error: ({event}) => event.error,
                }),
                'trackBookingError',
              ],
            },
            src: 'createEvent',
          },
        },
        error: {
          always: [
            {
              target: 'unavailableTimeSlot',
              guard: 'isTimeSlotUnavailable',
              reenter: true,
            },
            'retrySubmit',
          ],
        },
        retrySubmit: {
          on: {
            'form.submit': {
              target: 'submitting',
              reenter: true,
            },
          },
        },
        unavailableTimeSlot: {
          entry: 'trackMeetingSlotUnavailable',
          on: {
            'refetchAvailability.go': {
              target: '#booking.dateTimePicker.availabilityLoading',
              reenter: true,
            },
          },
        },
      },
      on: {
        'bookingForm.back': {
          target: '#booking.dateTimePicker.availabilitySlots',
        },
      },
    },

    requestMoreAvailabilitySuccess: {
      type: 'final',
      entry: assign({
        finalState: 'RequestMoreAvailabilitySuccess',
      }),
    },

    requestMoreAvailabilityForm: {
      initial: 'determineEntryStep',
      states: {
        determineEntryStep: {
          always: [
            {
              target: 'requestDetails',
              guard: 'shouldStartAtRequestDetailsStep',
            },
            {
              target: 'requesterInformation',
            },
          ],
        },
        requestDetails: {
          on: {
            'requestMoreAvailability.requestDetails.next': {
              target: 'requesterInformation',
            },
          },
        },
        requesterInformation: {
          on: {
            'requestMoreAvailability.requesterInfo.back': {
              target: 'requestDetails',
            },
            'requestMoreAvailability.requesterInfo.submit': {
              target: 'submittingSlotRequest',
            },
          },
        },
        submittingSlotRequest: {
          invoke: {
            input: ({
              context: {
                eventTemplate,
                eventDetails,
                timezone,
                session,
                language,
              },
              event,
            }) => {
              assertEvent(
                event,
                'requestMoreAvailability.requesterInfo.submit',
              );
              const templateId =
                eventTemplate?.id ||
                (eventDetails as IEvent)?.scheduled_from_template_id;

              if (templateId) {
                return {
                  templateId,
                  guestName: event.guestName,
                  guestEmail: event.guestEmail,
                  guestPhone: event.guestPhone,
                  timezone,
                  language,
                  sessionId: session?.id,
                  source: session?.source,
                  requestedSlots: event.requestedSlots,
                  requestReason: event.requestReason,
                  additionalQuestionAnswers:
                    event.additionalQuestionAnswers || [],
                };
              }
              throw new Error('Template id not found');
            },
            onDone: {
              target: '#booking.requestMoreAvailabilitySuccess',
              reenter: true,
            },
            onError: {
              target: 'error',
              actions: assign({
                error: ({event}) => event.error,
              }),
            },
            src: 'requestMoreAvailability',
          },
        },
        error: {
          always: [
            {
              target: '#booking.slotRequestAlreadyExists',
              guard: 'isSlotRequestAlreadyExists',
              reenter: true,
            },
            'retrySubmittingSlotRequest',
          ],
        },
        retrySubmittingSlotRequest: {
          on: {
            'requestMoreAvailability.requesterInfo.submit': {
              target: 'submittingSlotRequest',
              reenter: true,
            },
          },
        },
      },
      on: {
        'requestMoreAvailabilityForm.back': {
          target: '#booking.dateTimePicker.availabilitySlots',
        },
      },
    },

    slotRequestAlreadyExists: {
      on: {
        'refetchAvailability.go': {
          target: '#booking.dateTimePicker.availabilityLoading',
          reenter: true,
        },
      },
    },

    rescheduleForm: {
      initial: 'rescheduleReasonForm',
      on: {
        'rescheduleReasonForm.back': {
          target: 'dateTimePicker.availabilitySlots',
          reenter: true,
          actions: assign({
            rescheduleReason: undefined,
          }),
        },
      },
      states: {
        rescheduleReasonForm: {
          on: {
            'rescheduleReason.update': {
              actions: assign({
                rescheduleReason: ({event}) => event.reason,
              }),
            },
            'timeslot.confirm': 'rescheduleFormSubmitting',
          },
        },

        rescheduleFormSubmitting: {
          invoke: {
            input: ({
              context: {
                eventDetails,
                attendee,
                sequenceDays,
                currentSequenceNumber,
                timezone,
                rescheduleReason,
              },
              self,
            }) => {
              const sequenceNumber = currentSequenceNumber ?? 0;
              const selectedDay = sequenceDays?.find(
                (day) => day.sequenceNumber === sequenceNumber,
              );
              const timeSlots = selectedDay?.timeSlots;

              if (!timeSlots) {
                throw new Error('Time slots not found for current day');
              }

              const firstDate = Object.keys(timeSlots.free_time_slots || {})[0];
              const firstDateTimeList = timeSlots.free_time_slots[firstDate];
              const startTime = firstDateTimeList?.[0].start_time;

              const rootMachine = self.system.get('root');
              const rootSnapshot = rootMachine?.getSnapshot();

              // Get values from root context
              const aid = rootSnapshot?.context.aid;

              if ((eventDetails as IEvent)?.id && selectedDay?.selectedDate) {
                return {
                  eventId: (eventDetails as IEvent)?.id,
                  date: selectedDay.selectedDate,
                  startTime: selectedDay.selectedTimeSlot?.start_time || '',
                  timezone: timezone || '',
                  attendee: attendee || '',
                  firstAvailableDatetime: `${firstDate} ${startTime}`,
                  reason: rescheduleReason || '',
                  aid:
                    (eventDetails as IEvent)?.candidates?.[0]?.id || aid || '',
                };
              }
              throw new Error('Event id or selected date not found');
            },
            onDone: {
              target: '#booking.bookingSuccess',
              actions: [
                'trackBookingRescheduleConfirmed',
                assign({
                  eventDetails: ({event}) => event.output,
                }),
              ],
            },
            onError: {
              target: 'error',
              actions: assign({
                error: ({event}) => event.error,
              }),
            },
            src: 'updateEvent',
          },
        },

        error: {
          always: [
            {
              target: 'unavailableTimeSlotForReschedule',
              guard: 'isTimeSlotUnavailable',
            },
            {
              target: 'retryRescheduleFormSubmit',

              actions: assign({
                sequenceDays: ({context}): SequenceDay[] =>
                  updateSequenceDays(
                    context.sequenceDays,
                    context.currentSequenceNumber ?? 0,
                    {
                      selectedTimeSlot: undefined,
                    },
                  ) as SequenceDay[],
              }),

              reenter: true,
            },
          ],
        },

        unavailableTimeSlotForReschedule: {
          on: {
            'refetchAvailabilityForReschedule.go': {
              target: '#booking.dateTimePicker.availabilityLoading',
              reenter: true,
            },
          },
        },

        retryRescheduleFormSubmit: {
          on: {
            retryRescheduleFormSubmitting: 'rescheduleFormSubmitting',
          },
        },
      },
    },
  },
  output: ({context: {eventDetails, finalState, rescheduleReason}}) => ({
    eventDetails,
    finalState,
    rescheduleReason,
  }),
});

export type IBookingMachineSnapshot = SnapshotFrom<typeof bookingMachine>;
